import { Link } from "@tanstack/react-router";
import type {
  JobCategoryWithTags,
  JobTagWithCategory,
  JobWithRelations,
} from "@/types/db";
import type { LocationsResponse } from "@/lib/locations";
import SearchClient from "@/components/search/searchClient";
import Hero from "@/components/hero";
import EmailCaptureForm from "@/components/common/emailCaptureForm";
import BreadcrumbJsonLd from "@/components/common/breadcrumbJsonLd";
import { buildFaqSchema, buildMinimalFaqSchema } from "@/lib/faqSchema";
import type { TagStatsData } from "@/lib/faqSchema";
import { jsonLdSafe } from "@/lib/jsonLd";
import {
  getTagDisplayName,
  getTagEmptyStateCopy,
  getTagListingName,
  getTagSubtitle,
} from "@/lib/tagCopy";
import { CATEGORY_LABELS } from "@/lib/categoryLabels";

const SITE_URL = "https://datafirstjobs.com";

interface TagListingViewProps {
  tag: JobTagWithCategory;
  initialJobs: JobWithRelations[];
  initialTotal: number;
  tagStats: TagStatsData | null;
  jobCategoriesWithTags: JobCategoryWithTags[];
  locations: LocationsResponse;
  /** "remote" renders the /remote/{slug} variant: adds "Remote" prefix, remote
   * breadcrumb, and pre-selects the remote-data-jobs filter. */
  variant?: "default" | "remote";
  /**
   * Additional filter slugs from the URL query (`?tags=…`), applied on top of
   * the path-implicit slug(s). Path-implicit slugs are filtered out upstream
   * so they don't duplicate.
   */
  extraTagSlugs?: string[];
}

interface RelatedTag {
  name: string;
  /** Name adjusted for position-group plurals (used in page copy). */
  displayName: string;
  slug: string;
  bgColor?: string | null;
  jobCount: number;
}

export default function TagListingView({
  tag,
  initialJobs,
  initialTotal,
  tagStats,
  jobCategoriesWithTags,
  locations,
  variant = "default",
  extraTagSlugs = [],
}: TagListingViewProps) {
  const isRemote = variant === "remote";
  const tagRoute = isRemote ? "/remote/$slug" : "/$slug";
  const displayName = getTagDisplayName(tag);
  const listingName = getTagListingName(tag, { isRemote });
  const pathTagSlugs = isRemote ? ["remote-data-jobs", tag.slug] : [tag.slug];
  const initialTagSlugs = [...pathTagSlugs, ...extraTagSlugs];
  const searchClientKey = isRemote ? `remote-${tag.slug}` : tag.slug;

  const breadcrumbItems = isRemote
    ? [
        { name: "Home", url: SITE_URL },
        { name: "Remote Data Jobs", url: `${SITE_URL}/remote` },
        { name: listingName },
      ]
    : [{ name: "Home", url: SITE_URL }, { name: listingName }];

  // Find related tags from the same category (excluding current tag).
  // Sorted by live-job popularity (from tag_stats.total_jobs) so pills point
  // at the highest-signal pages; name is the tiebreaker for deterministic order.
  const category = jobCategoriesWithTags.find(
    (c: { id: number }) => c.id === tag.categoryId,
  );
  const allRelatedTags: RelatedTag[] =
    category?.jobTags
      ?.filter((t) => t.slug !== tag.slug)
      ?.map((t) => ({
        name: t.name,
        displayName: getTagDisplayName({
          name: t.name,
          category: { slug: category.slug },
        }),
        slug: t.slug,
        bgColor: category.bgColor,
        jobCount: t.jobCount ?? 0,
      })) ?? [];

  const relatedTags = [...allRelatedTags]
    .sort((a, b) => b.jobCount - a.jobCount || a.name.localeCompare(b.name))
    .slice(0, 5);

  // Prefer the curated label, fall back to the DB category name so any
  // category not in CATEGORY_LABELS (e.g. "position-group") still surfaces
  // the link to its `/browse/<slug>` parent. Tag pages for those slugs
  // were previously missing the link entirely.
  const browseAllLabel = category
    ? (CATEGORY_LABELS[category.slug] ?? category.name)
    : null;

  const heroTitle = isRemote ? (
    <>
      Remote <span className="text-polaris-orange">{displayName}</span> jobs
    </>
  ) : (
    <>
      <span className="text-polaris-orange">{displayName}</span> jobs
    </>
  );

  const heroSubtitle = getTagSubtitle(tag, { isRemote });
  const hasJobs = initialTotal > 0;
  const countLabel = initialTotal.toLocaleString();
  const emptyStateCopy = hasJobs
    ? null
    : getTagEmptyStateCopy(tag, { isRemote }).replace(
        "sign up above",
        "sign up below",
      );
  const emptyStateLinkPrefix = isRemote ? "Remote " : "";
  // Primary CTA scroll-anchors into SearchClient's `<section id="jobList">`.
  // Suppressed on empty pages since there's nothing to browse.
  const primaryCta = hasJobs
    ? {
        label: isRemote
          ? `Browse ${countLabel} remote jobs`
          : `Browse ${countLabel} jobs`,
        href: "#jobList",
      }
    : undefined;

  return (
    <>
      <BreadcrumbJsonLd items={breadcrumbItems} />
      {initialJobs.length > 0 && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: jsonLdSafe({
              "@context": "https://schema.org",
              "@type": "ItemList",
              name: listingName,
              // Match rendered items, not the total, so the schema and DOM agree.
              numberOfItems: initialJobs.length,
              itemListElement: initialJobs.map((job, i) => ({
                "@type": "ListItem",
                position: i + 1,
                url: `${SITE_URL}/${job.slug}`,
                name: job.role,
              })),
            }),
          }}
        />
      )}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: jsonLdSafe(
            tagStats
              ? buildFaqSchema(displayName, tagStats, {
                  isRemotePage: isRemote,
                })
              : buildMinimalFaqSchema(displayName, initialTotal, {
                  isRemotePage: isRemote,
                }),
          ),
        }}
      />
      <Hero
        title={heroTitle}
        subtitle={heroSubtitle}
        // Counter is conveyed via the primary CTA and by SearchClient below;
        // omit the Hero eyebrow to avoid two (potentially divergent) counts.
        relatedTags={relatedTags.map((r) => ({
          name: r.displayName,
          slug: r.slug,
          bgColor: r.bgColor,
        }))}
        hideBrowseLink
        hideEmailForm
        primaryCta={primaryCta}
      />
      {browseAllLabel && category && (
        <div className="mt-4 flex justify-center">
          <Link
            to="/browse/$slug"
            params={{ slug: category.slug }}
            className="group text-sm text-gray-500 transition-colors hover:text-gray-700"
          >
            Browse all {browseAllLabel}
            <span className="ml-0.5 inline-block transition-transform group-hover:translate-x-0.5">
              →
            </span>
          </Link>
        </div>
      )}
      {hasJobs ? (
        <SearchClient
          key={searchClientKey}
          jobCategoriesWithTags={jobCategoriesWithTags}
          locations={locations}
          initialJobs={initialJobs}
          initialTotal={initialTotal}
          initialTagSlugs={initialTagSlugs}
          pathTagSlugs={pathTagSlugs}
        />
      ) : (
        <section className="mx-auto mt-8 mb-20 max-w-3xl px-4 py-12 text-center">
          <p className="mb-6 text-lg text-gray-600">{emptyStateCopy}</p>
          <EmailCaptureForm />
          {relatedTags.length > 0 && (
            <div className="mt-10">
              <p className="mb-3 font-medium text-gray-700">
                Browse related categories:
              </p>
              <div className="flex flex-wrap justify-center gap-2">
                {relatedTags.map((related) => (
                  <Link
                    key={related.slug}
                    to={tagRoute}
                    params={{ slug: related.slug }}
                    className="rounded-full bg-gray-100 px-4 py-2 text-sm text-gray-700 transition-colors hover:bg-gray-200"
                  >
                    {emptyStateLinkPrefix}
                    {related.displayName} Jobs
                  </Link>
                ))}
              </div>
            </div>
          )}
        </section>
      )}
    </>
  );
}
